/**
 * Onboarding tour persistence (localStorage).
 *
 * Schema (ros.onboarding.v1):
 * {
 *   version: 1,
 *   tours: {
 *     [tourId]: { completed, skipped, lastStepIndex }
 *   }
 * }
 *
 * Legacy keys `ros.tour.<tourId>.done` are migrated on read.
 */

const STORAGE_KEY = "ros.onboarding.v1";
const LEGACY_PREFIX = "ros.tour.";

const DEFAULT_TOUR_STATE = Object.freeze({
  completed: false,
  skipped: false,
  lastStepIndex: 0,
});

function emptyStore() {
  return { version: 1, tours: {} };
}

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return emptyStore();
    return {
      version: 1,
      tours: parsed.tours && typeof parsed.tours === "object" ? parsed.tours : {},
    };
  } catch {
    return emptyStore();
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* private mode / quota */
  }
}

function migrateLegacy(tourId, state) {
  if (state.completed || state.skipped) return state;
  try {
    if (localStorage.getItem(`${LEGACY_PREFIX}${tourId}.done`) === "1") {
      return { ...state, completed: true };
    }
  } catch {
    /* ignore */
  }
  return state;
}

export function getTourState(tourId) {
  const store = readStore();
  const raw = store.tours[tourId] || {};
  const state = {
    completed: Boolean(raw.completed),
    skipped: Boolean(raw.skipped),
    lastStepIndex: Number.isFinite(raw.lastStepIndex) ? Math.max(0, raw.lastStepIndex) : 0,
  };
  return migrateLegacy(tourId, state);
}

export function setTourState(tourId, patch) {
  const store = readStore();
  const prev = getTourState(tourId);
  store.tours[tourId] = {
    ...DEFAULT_TOUR_STATE,
    ...prev,
    ...patch,
  };
  writeStore(store);
  return store.tours[tourId];
}

export function isTourFinished(tourId) {
  const s = getTourState(tourId);
  return s.completed || s.skipped;
}

export function isTourCompleted(tourId) {
  return getTourState(tourId).completed;
}

export function markTourCompleted(tourId) {
  setTourState(tourId, { completed: true, skipped: false });
  try {
    localStorage.setItem(`${LEGACY_PREFIX}${tourId}.done`, "1");
  } catch {
    /* ignore */
  }
}

export function markTourSkipped(tourId, lastStepIndex = 0) {
  setTourState(tourId, { skipped: true, completed: false, lastStepIndex });
  try {
    localStorage.setItem(`${LEGACY_PREFIX}${tourId}.done`, "1");
  } catch {
    /* ignore */
  }
}

export function setLastStepIndex(tourId, lastStepIndex) {
  setTourState(tourId, { lastStepIndex });
}

export function resetTourCompletion(tourId) {
  setTourState(tourId, { completed: false, skipped: false, lastStepIndex: 0 });
  try {
    localStorage.removeItem(`${LEGACY_PREFIX}${tourId}.done`);
  } catch {
    /* ignore */
  }
}
